import { defineNuxtPlugin, useState, useRequestEvent } from "#app";
import { parseCookies } from "h3";

/**
 * WebView Detection Plugin
 * 
 * Detects if the client is a WebView (embedded browser) and provides
 * server-side rendering context for conditional element hiding.
 */

interface WebViewState {
  isWebView: boolean;
  userAgent: string | null;
  detectionMethod: string | null;
}

export default defineNuxtPlugin((nuxtApp) => {
  console.log("Loading WebView detection plugin");

  // Create shared state that persists from SSR to CSR
  const webViewState = useState<WebViewState>("webView", () => ({
    isWebView: false,
    userAgent: null,
    detectionMethod: null,
  }));

  // WebView detection patterns
  const webViewPatterns = [
    // Generic WebView patterns
    /\bwv\b/i,
    /\bWebView\b/i,
    
    // Android WebView patterns - specific to actual WebViews
    /\bAndroid.*AppleWebKit.*Version\/[\d.]+.*Chrome\/[\d.]+.*Mobile.*wv\b/i,
    
    // Flutter WebView specific patterns
    /\bFlutter\b/i,
    /\bOnePassMobile\b/i,
    /\bOnePassApp\/Flutter\b/i,
    
    // iOS WebView patterns - FIXED: Only match if no Safari identifier
    /\b(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i,
    
    // Facebook WebView
    /\bFBAV\b/i,
    /\bFB_IAB\b/i,
    /\bFBIOS\b/i,
    
    // Instagram WebView
    /\bInstagram\b/i,
    
    // Twitter/X WebView
    /\bTwitter\b/i,
    
    // LinkedIn WebView
    /\bLinkedInApp\b/i,
    
    // Other social media WebViews
    /\bSnapchat\b/i,
    /\bPinterest\b/i,
    /\bTikTok\b/i,
    
    // React Native WebView (if different from main app)
    /\bReact Native\b/i,
    
    // Other embedded browsers
    /\bGSA\b/i, // Google Search App
    /\bDuckDuckGo\b/i,
    /\bSamsungBrowser\b/i,
    /\bLine\b/i,
    /\bWhatsApp\b/i,
  ];

  // Function to check if user agent matches WebView patterns
  const isWebViewUserAgent = (userAgent: string): boolean => {
    if (!userAgent) return false;
    
    // First, explicitly exclude regular Safari browsers
    if (/\bSafari\/[\d.]+\b/i.test(userAgent) && /\bVersion\/[\d.]+\b/i.test(userAgent)) {
      console.log("WebView detection: Excluding regular Safari browser");
      return false;
    }
    
    // Exclude Chrome iOS browsers (they contain CriOS identifier)
    if (/\bCriOS\/[\d.]+\b/i.test(userAgent)) {
      console.log("WebView detection: Excluding Chrome iOS browser");
      return false;
    }
    
    // Exclude Firefox iOS browsers (they contain FxiOS identifier)
    if (/\bFxiOS\/[\d.]+\b/i.test(userAgent)) {
      console.log("WebView detection: Excluding Firefox iOS browser");
      return false;
    }
    
    // Exclude Edge iOS browsers (they contain EdgiOS identifier)
    if (/\bEdgiOS\/[\d.]+\b/i.test(userAgent)) {
      console.log("WebView detection: Excluding Edge iOS browser");
      return false;
    }
    
    // Exclude Android Chrome browsers (they contain 'Chrome' and 'Safari' identifiers)
    if (/\bAndroid\b/i.test(userAgent) && /\bChrome\/[\d.]+\b/i.test(userAgent) && /\bSafari\/[\d.]+\b/i.test(userAgent)) {
      console.log("WebView detection: Excluding Android Chrome browser");
      return false;
    }
    
    // Exclude Android Firefox browsers (they contain 'Gecko' and 'Firefox' identifiers)
    if (/\bAndroid\b/i.test(userAgent) && /\bGecko\/[\d.]+\b/i.test(userAgent) && /\bFirefox\/[\d.]+\b/i.test(userAgent)) {
      console.log("WebView detection: Excluding Android Firefox browser");
      return false;
    }
    
    // Exclude Android Edge browsers (they contain 'EdgA' identifier)
    if (/\bEdgA\/[\d.]+\b/i.test(userAgent)) {
      console.log("WebView detection: Excluding Android Edge browser");
      return false;
    }
    
    // Check for WebView patterns
    const hasWebViewPattern = webViewPatterns.some(pattern => pattern.test(userAgent));
    
    // Additional checks for iOS WebView - must NOT contain Safari identifier OR known browser identifiers
    const isIOSWebView = /\b(iPhone|iPod|iPad)\b/i.test(userAgent) && 
                        !/\bSafari\/[\d.]+\b/i.test(userAgent) && 
                        !/\bCriOS\/[\d.]+\b/i.test(userAgent) &&  // Not Chrome iOS
                        !/\bFxiOS\/[\d.]+\b/i.test(userAgent) &&  // Not Firefox iOS
                        !/\bEdgiOS\/[\d.]+\b/i.test(userAgent) && // Not Edge iOS
                        /\bAppleWebKit\b/i.test(userAgent);
    
    // Additional checks for Android WebView - ONLY if it's actually a WebView, not a regular browser
    // Flutter WebViews should be caught by the webViewPatterns above
    const isAndroidWebView = /\bAndroid\b/i.test(userAgent) && 
                            /\bVersion\/[\d.]+\b/i.test(userAgent) && 
                            /\bChrome\/[\d.]+\b/i.test(userAgent) && 
                            /\bMobile\b/i.test(userAgent) &&
                            !/\bSafari\/[\d.]+\b/i.test(userAgent) && // Exclude browsers with Safari identifier
                            (/\bwv\b/i.test(userAgent) || /\bWebView\b/i.test(userAgent)); // Must explicitly contain WebView indicators
    
    const result = hasWebViewPattern || isIOSWebView || isAndroidWebView;
    
    if (result) {
      console.log("WebView detection: Detected as WebView", {
        hasWebViewPattern,
        isIOSWebView,
        isAndroidWebView,
        userAgent: `${userAgent.substring(0, 100)}...`
      });
    } else {
      console.log("WebView detection: NOT a WebView", {
        userAgent: `${userAgent.substring(0, 100)}...`,
        isIOSDevice: /\b(iPhone|iPod|iPad)\b/i.test(userAgent),
        hasSafari: /\bSafari\/[\d.]+\b/i.test(userAgent),
        hasCriOS: /\bCriOS\/[\d.]+\b/i.test(userAgent),
        hasFxiOS: /\bFxiOS\/[\d.]+\b/i.test(userAgent),
        hasEdgiOS: /\bEdgiOS\/[\d.]+\b/i.test(userAgent),
        isAndroidDevice: /\bAndroid\b/i.test(userAgent),
        hasAndroidChrome: /\bAndroid\b/i.test(userAgent) && /\bChrome\/[\d.]+\b/i.test(userAgent) && /\bSafari\/[\d.]+\b/i.test(userAgent),
        hasAndroidFirefox: /\bAndroid\b/i.test(userAgent) && /\bGecko\/[\d.]+\b/i.test(userAgent) && /\bFirefox\/[\d.]+\b/i.test(userAgent),
        hasAndroidEdge: /\bEdgA\/[\d.]+\b/i.test(userAgent),
        hasWebViewIndicators: /\bwv\b/i.test(userAgent) || /\bWebView\b/i.test(userAgent),
        hasFlutterIndicators: /\bFlutter\b/i.test(userAgent) || /\bOnePassMobile\b/i.test(userAgent)
      });
    }
    
    return result;
  };

  // SERVER-SIDE DETECTION
  if (typeof window === "undefined" && nuxtApp.ssrContext?.event) {
    const event = nuxtApp.ssrContext.event;
    const headers = event.node.req.headers;
    const cookies = parseCookies(event);
    const url = new URL(
      event.node.req.url || "",
      `http://${headers.host || "localhost"}`
    );
    
    console.log("SSR: WebView detection - checking headers and params");
    
    // CRITICAL: Check for X-GO-Client header or cookie (any value)
    const hasXGoClientHeader = headers["x-go-client"] !== undefined;
    const hasXGoClientCookie = cookies["X-GO-Client"] !== undefined || cookies["x-go-client"] !== undefined;
    
    // If X-GO-Client exists in either header or cookie, it's WebView
    if (hasXGoClientHeader || hasXGoClientCookie) {
      webViewState.value.isWebView = true;
      webViewState.value.detectionMethod = hasXGoClientHeader ? "x-go-client-header" : "x-go-client-cookie";
      webViewState.value.userAgent = headers["user-agent"] || "";
      
      console.log("SSR: WebView detected via X-GO-Client", {
        hasHeader: hasXGoClientHeader,
        headerValue: headers["x-go-client"],
        hasCookie: hasXGoClientCookie,
        cookieValue: cookies["X-GO-Client"] || cookies["x-go-client"],
      });
    } else {
      // Original detection logic continues if X-GO-Client not found
      // 1. Check for explicit WebView headers
      const hasWebViewHeader = headers["x-requested-with"] === "com.android.webview" ||
                             headers["x-go-webview"] === "true" ||
                             headers["x-webview"] === "true";
      
      // 2. Check for WebView query parameter
      const hasWebViewParam = url.searchParams.get("webview") === "true" ||
                            url.searchParams.get("embedded") === "true" ||
                            url.searchParams.get("inapp") === "true";
      
      // 3. Check User-Agent
      const userAgent = headers["user-agent"] || "";
      const hasWebViewUserAgent = isWebViewUserAgent(userAgent);
      
      // 4. Check for React Native but distinguish from main app
      const isReactNative = headers["x-go-client"] === "ReactNative";
      const isReactNativeWebView = isReactNative && (
        headers["x-go-context"] === "webview" ||
        headers["x-go-embedded"] === "true"
      );
      
      // Determine if WebView
      let detectionMethod = null;
      if (hasWebViewHeader) {
        webViewState.value.isWebView = true;
        detectionMethod = "header";
      } else if (hasWebViewParam) {
        webViewState.value.isWebView = true;
        detectionMethod = "query-param";
      } else if (isReactNativeWebView) {
        webViewState.value.isWebView = true;
        detectionMethod = "react-native-webview";
      } else if (hasWebViewUserAgent) {
        webViewState.value.isWebView = true;
        detectionMethod = "user-agent";
      }
      
      webViewState.value.userAgent = userAgent;
      webViewState.value.detectionMethod = detectionMethod;
    }
    
    console.log("SSR: WebView detection result:", {
      isWebView: webViewState.value.isWebView,
      method: webViewState.value.detectionMethod,
      userAgent: `${webViewState.value.userAgent.substring(0, 100)}...`,
      headers: {
        "x-requested-with": headers["x-requested-with"],
        "x-go-webview": headers["x-go-webview"],
        "x-go-client": headers["x-go-client"],
        "x-go-context": headers["x-go-context"],
      },
      cookies: {
        "X-GO-Client": cookies["X-GO-Client"],
        "x-go-client": cookies["x-go-client"],
      }
    });
  }

  // CLIENT-SIDE DETECTION (as fallback)
  if (typeof window !== "undefined") {
    // Only detect if not already detected server-side
    if (!webViewState.value.isWebView) {
      // Check for X-GO-Client cookie on client side
      const hasXGoClientCookie = document.cookie.split(';').some(cookie => {
        const trimmed = cookie.trim();
        return trimmed.startsWith('X-GO-Client=') || trimmed.startsWith('x-go-client=');
      });
      
      if (hasXGoClientCookie) {
        webViewState.value.isWebView = true;
        webViewState.value.userAgent = navigator.userAgent;
        webViewState.value.detectionMethod = "x-go-client-cookie-client";
        
        console.log("Client: WebView detected via X-GO-Client cookie");
      } else {
        const userAgent = navigator.userAgent;
        
        // Exclude Chrome iOS browsers from window object detection
        // Chrome iOS can have webkit.messageHandlers but should not be treated as WebView
        const isChromeIOS = /\bCriOS\/[\d.]+\b/i.test(userAgent);
        
        if (!isChromeIOS) {
          // Check for WebView indicators in window object
          const hasWebViewWindow = !!(
            window.ReactNativeWebView ||
            (window as any).Android ||
            (window as any).webkit?.messageHandlers ||
            (window.external && typeof (window.external as any).notify === "function")
          );
          
          // Check user agent
          const hasWebViewUserAgent = isWebViewUserAgent(userAgent);
          
          if (hasWebViewWindow || hasWebViewUserAgent) {
            webViewState.value.isWebView = true;
            webViewState.value.userAgent = userAgent;
            webViewState.value.detectionMethod = hasWebViewWindow ? "window-object" : "user-agent-client";
            
            console.log("Client: WebView detected", {
              method: webViewState.value.detectionMethod,
              userAgent: `${userAgent.substring(0, 100)}...`,
            });
          }
        } else {
          console.log("Client: Chrome iOS detected - skipping window object detection");
        }
      }
    }
  }

  // Create the context object to provide to the application
  const webViewContext = {
    get isWebView() {
      return webViewState.value.isWebView;
    },
    get userAgent() {
      return webViewState.value.userAgent;
    },
    get detectionMethod() {
      return webViewState.value.detectionMethod;
    },
    // Helper to check if specific platform WebView
    isPlatformWebView(platform: string): boolean {
      const ua = webViewState.value.userAgent || "";
      switch (platform.toLowerCase()) {
        case "facebook":
          return /\b(FBAV|FB_IAB|FBIOS)\b/i.test(ua);
        case "instagram":
          return /\bInstagram\b/i.test(ua);
        case "twitter":
          return /\bTwitter\b/i.test(ua);
        case "linkedin":
          return /\bLinkedInApp\b/i.test(ua);
        case "android":
          return /\bAndroid\b/i.test(ua) && this.isWebView;
        case "ios":
          return /\b(iPhone|iPod|iPad)\b/i.test(ua) && this.isWebView;
        default:
          return false;
      }
    },
  };

  console.log(`Plugin providing webView.isWebView = ${webViewContext.isWebView}`);

  // Provide to the application
  return {
    provide: {
      webView: webViewContext,
    },
  };
});
